import http from 'apis/config/http-service';
import { useCallback } from 'react';
import type { AccountManagerCandidate } from './types';

const endpoint = 'ums/v1/restaurants/account-managers/candidates';

export type AccountManagerOption = {
  value: number | 'unassigned';
  label: string;
};

export const UNASSIGNED_ACCOUNT_MANAGER_OPTION: AccountManagerOption = { value: 'unassigned', label: 'Unassigned' };

export const toAccountManagerOption = (candidate: AccountManagerCandidate): AccountManagerOption => ({
  value: candidate.id,
  label: candidate.email ? `${candidate.name} (${candidate.email})` : candidate.name,
});

// Async-search hook for ChakraAsyncSelect (raw http.get, no React Query). An empty input lists
// the first 50 active CMS users, so callers can pass `defaultOptions` to preload the dropdown.
// `includeUnassigned` prepends the "Unassigned" option (filter variant only).
export function useSearchAccountManagerCandidatesApi(options?: { includeUnassigned?: boolean }) {
  const includeUnassigned = options?.includeUnassigned ?? false;

  const loadOptions = useCallback(
    (inputValue: string, callback: (options: AccountManagerOption[]) => void) => {
      const search = inputValue.trim();

      http
        .get(endpoint, { params: { search: search || undefined } })
        .then((res) => {
          const candidates: AccountManagerOption[] = (res?.data?.data || []).map(toAccountManagerOption);
          const showUnassigned =
            includeUnassigned && (search === '' || 'unassigned'.includes(search.toLowerCase()));
          callback(showUnassigned ? [UNASSIGNED_ACCOUNT_MANAGER_OPTION, ...candidates] : candidates);
        })
        .catch(() => callback([]));
    },
    [includeUnassigned]
  );

  return { loadOptions };
}
